import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';

// Import analytics components
import { 
  AnalyticsDashboard, 
  ReportBuilder, 
  SavedReports, 
  useAnalytics 
} from '@/components/analytics';

// Import stores for data access
import { useAuthStore } from '@/store/authStore';
import { AiInsightsPanel } from '@/components/insights/AiInsightsPanel';
import { useAccountsStore } from '@/store/accountsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useCurrenciesStore } from '@/store/currenciesStore';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';

export default function Analytics() {
  const [activeView, setActiveView] = useState<'dashboard' | 'reports'>('dashboard');
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('last3Months');
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [areCurrenciesLoaded, setAreCurrenciesLoaded] = useState(false);

  // Store hooks
  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  const { initialized: categoriesInitialized  } = useCategoriesStore();
  const { currencies } = useCurrenciesStore();
  const { convertToDefault, defaultCurrency } = useCurrencyConversion();

  // Analytics hook - will be updated when we pass the transactions
  const { 
    periods, 
    isLoading: analyticsLoading, 
    lastUpdated,
    refresh: refreshAnalytics
  } = useAnalytics();

  // Get the main currency symbol
  const mainCurrency = useMemo(() => {
    const defaultCurrency = currencies.find(c => c.isDefault);
    return defaultCurrency || { code: 'USD', symbol: '$', name: 'US Dollar' };
  }, [currencies]);



  // Check if currencies are loaded
  useEffect(() => {
    if (currencies && currencies.length > 0 && !areCurrenciesLoaded) {
      console.log(`Analytics: ${currencies.length} currencies loaded`);
      setAreCurrenciesLoaded(true);
    }
  }, [currencies, areCurrenciesLoaded]);

  // Function to fetch ALL transactions for the selected period (similar to Dashboard)
  const fetchAllTransactionsForPeriod = useCallback(async (period: string) => {
    if (!user?.id || !areCurrenciesLoaded) return;

    setTransactionsLoading(true);
    try {
      // Get date range for the period to fetch data efficiently
      const now = new Date();
      let startDate: Date;
      
      switch (period) {
        case 'thisMonth':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'lastMonth':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          break;
        case 'last3Months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
          break;
        case 'last6Months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
          break;
        case 'thisYear':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        case 'allTime':
          // For all time, we'll fetch all transactions
          startDate = new Date(2020, 0, 1); // Start from a reasonable date
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      }

      console.log(`Analytics: Fetching transactions from ${startDate.toISOString()} for period: ${period}`);

      // Fetch ALL transactions for the period in batches
      let allFetchedTransactions: any[] = [];
      let hasMore = true;
      let cursor: string | null = null;
      const batchSize = 1000;

      while (hasMore) {
        let query = supabase
          .from('transactions')
          .select('*')
          .eq('user_id', user.id)
          .gte('date', startDate.toISOString())
          .order('date', { ascending: false })
          .limit(batchSize);

        if (cursor) {
          query = query.lt('date', cursor);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching transactions for analytics:', error);
          throw error;
        }

        if (data && data.length > 0) {
          allFetchedTransactions = [...allFetchedTransactions, ...data];
          cursor = data[data.length - 1].date;
          hasMore = data.length === batchSize;
          
          console.log(`Analytics: Fetched ${data.length} transactions, total: ${allFetchedTransactions.length}`);
        } else {
          hasMore = false;
        }
      }

      console.log(`Analytics: Successfully fetched ${allFetchedTransactions.length} total transactions`);
      setAllTransactions(allFetchedTransactions);
    } catch (error) {
      console.error('Error fetching all transactions for analytics:', error);
      setAllTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  }, [user?.id, areCurrenciesLoaded]);

  // Fetch transactions when period changes or when currencies are loaded
  useEffect(() => {
    if (areCurrenciesLoaded && user?.id && categoriesInitialized) {
      fetchAllTransactionsForPeriod(selectedPeriod);
    }
  }, [selectedPeriod, areCurrenciesLoaded, user?.id, categoriesInitialized, fetchAllTransactionsForPeriod]);

  const handleCreateReport = () => {
    setShowReportBuilder(true);
  };

  const handleReportSaved = () => {
    setShowReportBuilder(false);
    // Optionally switch to reports view to see the new report
    setActiveView('reports');
  };

  const handlePeriodChange = (periodValue: string) => {
    setSelectedPeriod(periodValue);
  };

  const handleRefresh = () => {
    fetchAllTransactionsForPeriod(selectedPeriod);
    refreshAnalytics();
  };

  // Find the current period object
  const currentPeriod = periods.find(p => p.value === selectedPeriod) || periods[2];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-primary via-primary/5 to-secondary/5 dark:from-background-dark-primary dark:via-primary-dark/5 dark:to-secondary-dark/5">
      {/* Header - Following design reference page header pattern */}
      <div className="flex-shrink-0 border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm mx-4 mt-4">
        <div className="px-4 sm:px-8 py-5 sm:py-6">
          {/* Title and Description */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex-shrink-0">
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                  Analytics & Insights
                </h1>
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 dark:bg-primary-dark/10 dark:text-primary-dark dark:border-primary-dark/20">
                  Enhanced
                </Badge>
              </div>
              <div className="flex items-center space-x-6 text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                <span className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-positive dark:bg-positive-dark rounded-full animate-pulse"></div>
                  <span>Live Data</span>
                </span>
                <span>{format(new Date(), 'MMMM dd, yyyy')}</span>
                {lastUpdated && (
                  <span>Updated {format(lastUpdated, 'HH:mm')}</span>
                )}
                {allTransactions.length > 0 && (
                  <span className="hidden lg:flex items-center space-x-1 text-xs">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span>{allTransactions.length} transactions loaded</span>
                  </span>
                )}
                {mainCurrency && (
                  <span className="hidden lg:flex items-center space-x-1 text-xs">
                    <div className="w-2 h-2 bg-secondary rounded-full"></div>
                    <span>Currency: {mainCurrency.code} ({mainCurrency.symbol})</span>
                  </span>
                )}
              </div>
            </div>

            {/* Desktop - Primary Action Button */}
            <div className="hidden sm:block">
              <Button
                onClick={handleCreateReport}
                className="shadow-sm hover:shadow-md transition-all duration-200 rounded-xl h-11 bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Report
              </Button>
            </div>
          </div>

          {/* Action Buttons and Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 order-2 sm:order-1 sm:flex-nowrap">
              {/* Period Selector */}
              <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="flex-1 sm:flex-none w-[180px] bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border border-border bg-background-secondary dark:border-border-dark dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                  {periods.map((period) => (
                    <SelectItem key={period.value} value={period.value} className="rounded-lg px-2 py-1.5 text-sm hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark">
                      {period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* View Toggle */}
              <div className="flex rounded-xl bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border border-border dark:border-border-dark p-1">
                <Button
                  variant={activeView === 'dashboard' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('dashboard')}
                  className={cn(
                    "px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg",
                    activeView === 'dashboard'
                      ? "bg-primary text-white shadow-sm dark:bg-primary-dark"
                      : "text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
                  )}
                >
                  Dashboard
                </Button>
                <Button
                  variant={activeView === 'reports' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('reports')}
                  className={cn(
                    "px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg",
                    activeView === 'reports'
                      ? "bg-primary text-white shadow-sm dark:bg-primary-dark"
                      : "text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
                  )}
                >
                  Reports
                </Button>
              </div>

              {/* Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={transactionsLoading || analyticsLoading}
                className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm h-10"
              >
                <RefreshCw className={cn("w-4 h-4", (transactionsLoading || analyticsLoading) && "animate-spin")} />
              </Button>

              {/* Mobile only Primary Action button */}
              <Button
                onClick={handleCreateReport}
                size="sm"
                className="flex-1 sm:hidden shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Report
              </Button>
            </div>

            {/* Search Bar (only for reports view) */}
            {activeView === 'reports' && (
              <div className="relative flex-1 order-1 sm:order-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                <Input
                  placeholder="Search reports and insights..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 w-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-border dark:border-border-dark rounded-xl shadow-sm text-sm"
                />
              </div>
            )}
          </div>

          {/* Loading indicator when fetching data */}
          {transactionsLoading && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center space-x-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span>Loading transactions for {currentPeriod.label.toLowerCase()}...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'dashboard' && (
          <div className="animate-in fade-in-50 duration-500">
            <AnalyticsDashboard
              transactions={allTransactions}
              selectedPeriod={currentPeriod}
              isLoading={transactionsLoading}
              mainCurrencySymbol={mainCurrency.symbol}
              convertToDefault={convertToDefault}
              accounts={accounts}
              defaultCurrency={defaultCurrency}
            />
            <div className="mt-6">
              <AiInsightsPanel />
            </div>
          </div>
        )}

        {activeView === 'reports' && (
          <div className="animate-in fade-in-50 duration-500">
            <SavedReports 
              onCreateNew={handleCreateReport}
              transactions={allTransactions}
              selectedPeriod={currentPeriod}
              mainCurrencySymbol={mainCurrency.symbol}
              convertToDefault={convertToDefault}
              accounts={accounts}
              defaultCurrency={defaultCurrency}
            />
          </div>
        )}
      </div>

      {/* Report Builder Dialog */}
      <Dialog open={showReportBuilder} onOpenChange={setShowReportBuilder}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl p-0">
          {/* Hidden accessibility elements for screen readers */}
          <DialogHeader className="sr-only">
            <DialogTitle>Create New Report</DialogTitle>
            <DialogDescription>
              Build custom analytics reports to track your financial data using a step-by-step wizard.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6">
            <ReportBuilder 
              onSave={handleReportSaved}
              onCancel={() => setShowReportBuilder(false)}
              transactions={allTransactions}
              selectedPeriod={currentPeriod}
              mainCurrencySymbol={mainCurrency.symbol}
              convertToDefault={convertToDefault}
              accounts={accounts}
              defaultCurrency={defaultCurrency}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}